// Port of SuiteModel/TextCleanup.swift (and DashStyle's census): common mistakes in prose, found and fixed — runs of
// spaces, extra empty lines between paragraphs, full stops for an ellipsis, dashes that don't match the manuscript's
// style, paragraphs/sentences/speeches that start with a small letter, and dialogue in the other quotation marks. Works
// on the dialect's Markdown source, where one blank line separates blocks. Positions are Unicode-scalar offsets. Exact:
// every rule, exception and message is the Swift's. With the Revise room's typography (TextCleanup+Typography.swift:
// curly quotes, primes, en dashes in ranges, the multiplication sign, symbols, underlining) and mistakes
// (TextCleanup+Mistakes.swift: doubled words, dialogue punctuation, spacing round punctuation, brackets).
import { linkScalarRanges, MAX_VERSE_INDENT, paragraphFence, parseListMarker, spanStyle } from '../proseMarkdown.ts';
import { graphemes, isCFWhitespace, trimCFWhitespace } from '../unicode.ts';
import { quoteStyleOf, type QuoteStyle } from '../text/dialogueBalance.ts';
import { dashOf, dashStyleExample, isSpaced, usualFromCensus, type DashCensus, type DashStyle } from './dashStyle.ts';
import { analyseQuotes, quoteProblemMessage, QUOTE_MARKERS } from '../text/quoteMarks.ts';
import { curled, quotations, scalarLines, type Quotation } from '../text/quotations.ts';
import { fromScalars, isAlphabetic, isDigit, isLowercase, isUppercase, isWhitespace, titlecase, toScalars } from '../text/scalars.ts';

export type CleanupCheck = 'spaces' | 'empty-lines' | 'capitals' | 'dialogue-quotes' | 'ellipses' | 'dashes' | 'small-caps-times'
  | 'curly-quotes' | 'primes' | 'en-dash-ranges' | 'multiplication' | 'symbols' | 'underlining' | 'doubled-words'
  | 'dialogue-punctuation' | 'punctuation-spacing' | 'brackets' | 'verse-spaces';

/** `Check.allCases`. */
export const CLEANUP_CHECKS: readonly CleanupCheck[] = [
  'spaces', 'empty-lines', 'capitals', 'dialogue-quotes', 'ellipses', 'dashes', 'small-caps-times', 'curly-quotes', 'primes',
  'en-dash-ranges', 'multiplication', 'symbols', 'underlining', 'doubled-words', 'dialogue-punctuation', 'punctuation-spacing', 'brackets',
  'verse-spaces',
];

/** `Check.standard`: every check but those a writer may mean (underlining), what Clean Up and the Revise room run unless
 *  told otherwise. */
export const STANDARD_CLEANUP_CHECKS: readonly CleanupCheck[] = CLEANUP_CHECKS.filter((c) => c !== 'underlining');

/** `RevisePass`: the passes the Revise room works in, from the certain to the arguable. */
export type RevisePass = 'mistakes' | 'typography' | 'consistency' | 'style';
export const REVISE_PASSES: readonly RevisePass[] = ['mistakes', 'typography', 'consistency', 'style'];
export const revisePassName = (p: RevisePass): string =>
  ({ mistakes: 'Mistakes', typography: 'Typography', consistency: 'Consistency', style: 'Style' })[p];
export const revisePassSummary = (p: RevisePass): string => ({
  mistakes: 'Anything wrong. Checked as you go.', typography: 'Set as a book, not typed. Every fix is certain.',
  consistency: 'The book agreeing with itself.', style: 'Could it read better? Checked when you ask.',
})[p];

/** `Check.pass`: the Revise room's pass a check belongs to. */
export const cleanupCheckPass = (c: CleanupCheck): RevisePass =>
  (['capitals', 'doubled-words', 'dialogue-punctuation', 'punctuation-spacing', 'brackets', 'verse-spaces'] as CleanupCheck[]).includes(c) ? 'mistakes' : 'typography';

export const cleanupCheckName = (c: CleanupCheck): string => ({
  spaces: 'Extra spaces', 'empty-lines': 'Empty lines between paragraphs', capitals: 'Small letters starting sentences',
  'dialogue-quotes': 'Quotation marks for dialogue', ellipses: 'Full stops for an ellipsis', dashes: 'Dash style',
  'small-caps-times': 'A.M. and P.M. in small caps', 'curly-quotes': 'Curly quotes and apostrophes', primes: 'Primes for feet and inches',
  'en-dash-ranges': 'En dashes in ranges', multiplication: 'Multiplication sign', symbols: 'Trademark and copyright symbols',
  underlining: 'Underlining', 'doubled-words': 'Doubled words', 'dialogue-punctuation': 'Dialogue punctuation',
  'punctuation-spacing': 'Spacing round punctuation', brackets: 'Brackets never closed', 'verse-spaces': 'Spaces used as indents in verse',
})[c];

export const cleanupCheckSummary = (c: CleanupCheck): string => ({
  spaces: 'Runs of spaces become one; spaces at the start or end of a paragraph go.',
  'empty-lines': 'Paragraphs keep their usual gap; extra empty lines between them go. Use *** for a scene break.',
  capitals: 'A paragraph, sentence or speech that starts with a small letter gets a capital.',
  'dialogue-quotes': 'Dialogue in the other marks is switched, and quotes inside it take the opposite marks.',
  ellipses: 'Three full stops (or . . .) become an ellipsis character: …',
  dashes: 'Dashes between words follow one style, and double hyphens become dashes. Ranges like 1990–95 keep their en dash.',
  'small-caps-times': 'a.m. and p.m. (or A.M. and P.M. after a time) are set in small capitals, as books set them.',
  'curly-quotes': 'Straight quotes and apostrophes are curled by context (’70s, ’til), and curly quotes facing the wrong way are turned.',
  primes: 'Feet and inches after a number take primes, not quotation marks: 5′10″.',
  'en-dash-ranges': 'A hyphen between numbers that make a range becomes an en dash: 1990–95, pages 10–12.',
  multiplication: 'An x between numbers becomes a multiplication sign: 3 × 5.',
  symbols: '(tm) and (r) become ™ and ®, and (c) before a year becomes ©.',
  underlining: 'Underlined words: books set emphasis in italics. Off unless you turn it on, as underlining may be meant.',
  'doubled-words': 'The same word twice in a row: “the the”.',
  'dialogue-punctuation': 'A full stop where a tag carries the sentence on (“Stop.” he said), no punctuation before a tag, or a tag that starts with a capital.',
  'punctuation-spacing': 'A space before a comma or full stop, no space after one, and doubled commas.',
  brackets: 'A bracket opened and never closed in its paragraph, or closed and never opened.',
  'verse-spaces': 'Spaces at the start of a line of verse become indent steps (a step for every two to four), which hold in every font and format.',
})[c];

export interface CleanupOptions {
  checks?: Iterable<CleanupCheck>;
  /** The marks dialogue should use; a quote inside dialogue uses the other kind. */
  quoteStyle?: QuoteStyle;
  /** How dashes between words are set. */
  dashStyle?: DashStyle;
  /** The text's language (a BCP 47 code, as the project's `locale`): French sets a space before ; : ! and ?. */
  language?: string | null;
}

interface Options { checks: Set<CleanupCheck>; quoteStyle: QuoteStyle; dashStyle: DashStyle; language: string | null }
const resolved = (o: CleanupOptions = {}): Options =>
  ({ checks: new Set(o.checks ?? STANDARD_CLEANUP_CHECKS), quoteStyle: o.quoteStyle ?? 'double', dashStyle: o.dashStyle ?? 'closed', language: o.language ?? null });

export interface CleanupFinding {
  check: CleanupCheck;
  /** Scalar offsets [from, to). */
  range: [number, number];
  /** What a fix puts there (null when only the writer can tell, so it's listed instead). */
  replacement: string | null;
  message: string;
}

export interface CleanupReport {
  findings: CleanupFinding[];
}

/** How many places a fix would change (for one check, or all). */
export const fixableCount = (r: CleanupReport, check?: CleanupCheck) =>
  r.findings.filter((f) => f.replacement !== null && (check === undefined || f.check === check)).length;
/** `Report.toReview`: findings only the writer can settle. */
export const toReview = (r: CleanupReport) => r.findings.filter((f) => f.replacement === null);
export const isClean = (r: CleanupReport) => r.findings.length === 0;

/** The order fixes run in: spacing first, so the later checks see tidy paragraphs; primes before quotes are curled; ranges
 *  before dashes; dialogue's marks before its punctuation and capitals. */
const ORDER: readonly CleanupCheck[] = [
  'spaces', 'empty-lines', 'primes', 'curly-quotes', 'ellipses', 'en-dash-ranges', 'dashes', 'multiplication', 'symbols',
  'small-caps-times', 'underlining', 'doubled-words', 'punctuation-spacing', 'dialogue-quotes', 'dialogue-punctuation', 'capitals', 'brackets',
  'verse-spaces',
];

const byStart = (a: CleanupFinding, b: CleanupFinding) => a.range[0] - b.range[0];

/** `TextCleanup.check`. */
export function checkText(text: string, options: CleanupOptions = {}): CleanupReport {
  const o = resolved(options);
  const s = toScalars(text);
  const findings = ORDER.filter((c) => o.checks.has(c)).flatMap((c) => find(c, s, o));
  return { findings: findings.sort(byStart) };
}

export interface CleanupSurvey {
  /** One report per text, in the order given. */
  reports: CleanupReport[];
  quoteStyle: QuoteStyle;
  dashStyle: DashStyle;
  /** How many dashes between words are in each style now (`DashStyle.census`). */
  dashCensus: DashCensus;
}

/** All the findings of a survey together. */
export const surveyTotal = (s: CleanupSurvey): CleanupReport => ({ findings: s.reports.flatMap((r) => r.findings) });

/** `TextCleanup.survey`: many texts at once (a manuscript), each text's dashes found once for the census, the usual
 *  style and the check. Styles not given are the ones the texts mostly use. */
export function surveyTexts(texts: readonly string[], o: { checks?: Iterable<CleanupCheck>; quoteStyle?: QuoteStyle | null; dashStyle?: DashStyle | null; language?: string | null } = {}): CleanupSurvey {
  const checks = new Set(o.checks ?? STANDARD_CLEANUP_CHECKS);
  const scalars = texts.map(toScalars);
  const lists = scalars.map(dashList);
  const census: DashCensus = {};
  scalars.forEach((s, i) => {
    for (const [style, n] of Object.entries(dashes(s, 'closed', lists[i]).census)) census[style as DashStyle] = (census[style as DashStyle] ?? 0) + n;
  });
  const dashStyle = o.dashStyle ?? usualFromCensus(census);
  const quoteStyle = o.quoteStyle ?? quoteStyleOf(texts);
  const options: Options = { checks, quoteStyle, dashStyle, language: o.language ?? null };
  const reports = scalars.map((s, i) => {
    const findings = ORDER.filter((c) => checks.has(c))
      .flatMap((c) => (c === 'dashes' ? leavingLinksAlone(dashes(s, dashStyle, lists[i]).findings, 'dashes', s) : find(c, s, options)));
    return { findings: findings.sort(byStart) };
  });
  return { reports, quoteStyle, dashStyle, dashCensus: census };
}

/** `TextCleanup.fixed`: everything fixable fixed; findings for the writer are left alone. */
export function fixedText(text: string, options: CleanupOptions = {}): string {
  const o = resolved(options);
  let s = toScalars(text);
  let changed = false;
  for (const check of ORDER) {
    if (!o.checks.has(check)) continue;
    const edits = find(check, s, o).filter((f) => f.replacement !== null).sort((a, b) => b.range[0] - a.range[0]);
    for (const e of edits) {
      s = [...s.slice(0, e.range[0]), ...toScalars(e.replacement!), ...s.slice(e.range[1])];
      changed = true;
    }
  }
  return changed ? fromScalars(s) : text;
}

function find(check: CleanupCheck, s: string[], o: Options): CleanupFinding[] {
  return leavingLinksAlone(findWithin(check, s, o), check, s);
}

/** Swift's `Range.overlaps`: an element in common (an empty range overlaps nothing). */
const overlaps = (a: [number, number], b: [number, number]) => !(b[1] <= a[0] || a[1] <= b[0] || a[0] === a[1] || b[0] === b[1]);
const contains = (r: [number, number], x: number) => r[0] <= x && x < r[1];

/** `leavingLinksAlone`: nothing touches a link's address or syntax (a `--` or `...` or `a.m.` in an address stays as it
 *  is), and no span goes inside a link's words, as spans can't sit there. */
function leavingLinksAlone(findings: CleanupFinding[], check: CleanupCheck, s: string[]): CleanupFinding[] {
  const links = linkScalarRanges(s);
  if (links.length === 0) return findings;
  return findings.filter((f) => !links.some((link) => (check === 'small-caps-times'
    ? overlaps(link.whole, f.range) || contains(link.whole, f.range[0])
    : link.syntax.some((r) => overlaps(r, f.range) || (f.range[0] === f.range[1] && contains(r, f.range[0]))))));
}

function findWithin(check: CleanupCheck, s: string[], o: Options): CleanupFinding[] {
  switch (check) {
    case 'spaces': return spaces(s, o);
    case 'empty-lines': return emptyLines(s).findings;
    case 'capitals': return capitals(s);
    case 'dialogue-quotes': return dialogueQuotes(s, o.quoteStyle);
    case 'ellipses': return ellipses(s);
    case 'dashes': return dashes(s, o.dashStyle).findings;
    case 'small-caps-times': return smallCapsTimes(s);
    case 'curly-quotes': return curlyQuotes(s);
    case 'primes': return primes(s);
    case 'en-dash-ranges': return enDashRanges(s);
    case 'multiplication': return multiplication(s);
    case 'symbols': return symbols(s);
    case 'underlining': return underlining(s);
    // Line ends in verse aren't sentence ends, and a poet's brackets and repeats are their own.
    case 'doubled-words': return outsideVerse(doubledWords(s), s);
    case 'dialogue-punctuation': return outsideVerse(dialoguePunctuation(s), s);
    case 'punctuation-spacing': return punctuationSpacing(s, o.language);
    case 'brackets': return outsideVerse(brackets(s), s);
    case 'verse-spaces': return verseSpaces(s);
  }
}

// MARK: Verse

/** `TextCleanup.verseLines`: the content lines inside `::: verse` fences (the fence lines themselves left out). */
export function verseLines(s: readonly string[]): [number, number][] {
  const out: [number, number][] = [];
  let inVerse = false;
  for (const line of scalarLines(s)) {
    const trimmed = trimCFWhitespace(fromScalars(s, line[0], line[1]));
    if (inVerse) {
      if (trimmed === ':::') inVerse = false;
      else out.push(line);
    } else if (paragraphFence(trimmed)?.kind.type === 'verse') {
      inVerse = true;
    }
  }
  return out;
}

/** Findings that don't fall on a line of verse. */
function outsideVerse(findings: CleanupFinding[], s: readonly string[]): CleanupFinding[] {
  const verse = verseLines(s);
  if (verse.length === 0) return findings;
  return findings.filter((f) => !verse.some(([lo, hi]) => (lo < f.range[1] && f.range[0] < hi) || (lo <= f.range[0] && f.range[0] < hi)));
}

/** Spaces used to step a line of verse: after any tabs, a run of spaces becomes a tab for every two to four of them
 *  (eight spaces two steps); one space alone goes. */
function verseSpaces(s: string[]): CleanupFinding[] {
  const out: CleanupFinding[] = [];
  for (const [lo, hi] of verseLines(s)) {
    let i = lo;
    let tabs = 0;
    while (i < hi && s[i] === '\t') { i++; tabs++; }
    let j = i;
    while (j < hi && (s[j] === ' ' || s[j] === '\u00A0')) j++;
    if (j <= i || j >= hi) continue;
    const spaces = j - i;
    const steps = Math.min(MAX_VERSE_INDENT - tabs, spaces < 2 ? 0 : Math.floor((spaces + 3) / 4));
    out.push({ check: 'verse-spaces', range: [i, j], replacement: '\t'.repeat(Math.max(0, steps)),
      message: spaces === 1 ? 'A space at the start of a line of verse.' : `${spaces} spaces used to step a line of verse.` });
  }
  return out;
}

// MARK: Times of day

/** Words that end a time written out (`six`, `six-thirty`, `five o’clock`), so `P.M.` after them is a time. */
const TIME_WORDS: ReadonlySet<string> = new Set([
  'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve',
  'fifteen', 'twenty', 'thirty', 'forty', 'fifty', 'o’clock', "o'clock",
]);

/** a.m. and p.m. set in a small-caps span, in lower case so the span draws them as small capitals: `[a.m.]{.sc}`. Lower
 *  case is always a time; capitals only after one (`6 P.M.`, `six P.M.`), since `the P.M.` is a prime minister and
 *  `Jane Smith, A.M.` a degree. Nothing inside a span changes: one already in small caps is done, and spans (underline,
 *  highlight) don't nest. */
function smallCapsTimes(s: string[]): CleanupFinding[] {
  const spans = spanRanges(s);
  const findings: CleanupFinding[] = [];
  let i = 0;
  while (i + 4 <= s.length) {
    const c = s[i];
    const upper = c === 'A' || c === 'P';
    const time = (upper || c === 'a' || c === 'p') && s[i + 1] === '.' && s[i + 2] === (upper ? 'M' : 'm') && s[i + 3] === '.'
      && (i === 0 || !(isAlphabetic(s[i - 1]) || s[i - 1] === '.' || s[i - 1] === '\\'))
      && (i + 4 === s.length || !(isAlphabetic(s[i + 4]) || isDigit(s[i + 4])))
      && !spans.some(([from, to]) => i >= from && i < to)
      && (!upper || followsTime(s, i));
    if (!time) { i++; continue; }
    const letter = c.toLowerCase();
    findings.push({ check: 'small-caps-times', range: [i, i + 4], replacement: `[${letter}.m.]{.sc}`, message: `${letter}.m. not in small capitals.` });
    i += 4;
  }
  return findings;
}

/** Whether the word before `i` (past spaces) is a time: it has a digit (`6`, `6:30`) or ends in a number word. */
function followsTime(s: readonly string[], i: number): boolean {
  let end = i;
  while (end > 0 && DASH_SPACES.has(s[end - 1])) end--;
  let start = end;
  while (start > 0 && (isAlphabetic(s[start - 1]) || isDigit(s[start - 1]) || ['’', "'", '-', ':', '.'].includes(s[start - 1]))) start--;
  const word = fromScalars(s as string[], start, end).toLowerCase();
  if (/[0-9]/.test(word)) return true;
  return TIME_WORDS.has(word.split('-').filter(Boolean).at(-1) ?? '');
}

/** Where the text's spans are, from `[` to the end of `]{…}`, found as the dialect's parser finds them: the first
 *  unescaped `]` in the paragraph with no `[` before it, followed by classes the dialect knows. */
export function spanRanges(s: readonly string[]): [number, number][] {
  const ranges: [number, number][] = [];
  let i = 0;
  while (i < s.length) {
    if (s[i] === '\\') { i += 2; continue; }
    if (!(s[i] === '[' && (i + 1 >= s.length || s[i + 1] !== '^'))) { i++; continue; }
    let j = i + 1;
    let found = false;
    while (j < s.length && s[j] !== '[' && !(s[j] === '\n' && j + 1 < s.length && s[j + 1] === '\n')) {
      if (s[j] === '\\') { j += 2; continue; }
      if (s[j] === ']') {
        const length = spanClose(s, j, s.length);
        if (length !== null) {
          ranges.push([i, j + length]);
          i = j + length;
          found = true;
        }
        break;
      }
      j++;
    }
    if (!found) i++;
  }
  return ranges;
}

// MARK: Ellipses

/** Three full stops, or `. . .`, for an ellipsis: always the character instead. Four become `….`; other runs stay. */
function ellipses(s: string[]): CleanupFinding[] {
  const findings: CleanupFinding[] = [];
  let i = 0;
  while (i < s.length) {
    if (!(s[i] === '.' && (i === 0 || s[i - 1] !== '.'))) { i++; continue; }
    let run = i;
    while (run < s.length && s[run] === '.') run++;
    const n = run - i;
    if (n === 3 || n === 4) {
      findings.push({ check: 'ellipses', range: [i, run], replacement: n === 3 ? '…' : '….', message: 'Three full stops for an ellipsis.' });
      i = run;
      continue;
    }
    // Spaced: `. . .` (and `. . . .`), not part of a longer run.
    if (n === 1 && i + 4 < s.length && s[i + 1] === ' ' && s[i + 2] === '.' && s[i + 3] === ' ' && s[i + 4] === '.' &&
      (i + 5 >= s.length || s[i + 5] !== '.')) {
      let end = i + 5;
      let replacement = '…';
      if (end + 1 < s.length && s[end] === ' ' && s[end + 1] === '.' && (end + 2 >= s.length || s[end + 2] !== '.')) {
        end += 2;
        replacement = '….';
      }
      findings.push({ check: 'ellipses', range: [i, end], replacement, message: 'Spaced full stops for an ellipsis.' });
      i = end;
      continue;
    }
    i = run;
  }
  return findings;
}

// MARK: Dashes

const DASH_SPACES: ReadonlySet<string> = new Set([' ', '\u00A0', '\u2009', '\u200A', '\u202F']);
const DASH_CHARACTERS: ReadonlySet<string> = new Set(['-', '—', '–']);

/** A dash set between words (or where one should be). */
export interface Dash {
  range: [number, number];
  dash: [number, number];
  /** Letters, digits or emphasis markers touch the dash (past any spaces) on this side, or a quotation that opens after
   *  it or closes before it (`—“sheep”`, `”—`). */
  wordBefore: boolean;
  wordAfter: boolean;
  style: DashStyle | null;
  hyphens: boolean;
}

export function dashList(s: readonly string[]): Dash[] {
  const found: Dash[] = [];
  const markers = listMarkers(s);
  const word = (x: string) => isAlphabetic(x) || isDigit(x) || x === '*' || x === '_';
  for (const [lo, hi] of scalarLines(s)) {
    // A line of dashes (hyphens, em or en) is a rule or scene break.
    let rule = true;
    for (let k = lo; k < hi; k++) if (!(DASH_CHARACTERS.has(s[k]) || DASH_SPACES.has(s[k]) || s[k] === '\t')) { rule = false; break; }
    if (rule) continue;
    let i = lo + (markers.get(lo) ?? 0); // a list's "–⇥" is a bullet
    while (i < hi) {
      const c = s[i];
      let end = i + 1;
      let hyphens: boolean;
      if (c === '—' || c === '–') {
        // Two or more together (`Mr B——`, a 2- or 3-em dash for what's left out) stay as they are.
        while (end < hi && (s[end] === '—' || s[end] === '–')) end++;
        if (end - i !== 1) { i = end; continue; }
        hyphens = false;
      } else if (c === '-') {
        while (end < hi && s[end] === '-') end++;
        if (end - i > 3) { i = end; continue; }
        hyphens = true;
      } else {
        i++;
        continue;
      }
      let ls = i;
      while (ls > lo && DASH_SPACES.has(s[ls - 1])) ls--;
      let re = end;
      while (re < hi && DASH_SPACES.has(s[re])) re++;
      const before = ls > lo && (word(s[ls - 1]) || [')', '”', '’'].includes(s[ls - 1]));
      const after = re < hi && (word(s[re]) || ['(', '“', '‘'].includes(s[re]));
      const spacedBefore = ls < i, spacedAfter = re > end;
      if (hyphens) {
        // One hyphen is a hyphen unless it stands between spaces; two or three are a dash. A spaced hyphen between the
        // numbers of a range (`1990 - 1995`) is the en-dash check's.
        if (end - i === 1 && (!(spacedBefore && spacedAfter && before && after) || rangeHyphen(s, i, 1) !== null)) { i = end; continue; }
      } else if (c === '–' && !spacedBefore && !spacedAfter) {
        // An en dash closed up between words or numbers: a range (1990–95, London–Paris).
        i = end;
        continue;
      }
      let style: DashStyle | null = null;
      if (!hyphens && before && after) {
        if (c === '—' && !spacedBefore && !spacedAfter) style = 'closed';
        if (spacedBefore && spacedAfter) style = c === '—' ? 'spaced-em' : 'spaced-en';
      }
      found.push({ range: [ls, re], dash: [i, end], wordBefore: before, wordAfter: after, style, hyphens });
      i = end;
    }
  }
  return found;
}

/** Dashes that don't match `style`, each with its fix, and how many dashes are already in each style. */
export function dashes(s: readonly string[], style: DashStyle, list?: Dash[]): { findings: CleanupFinding[]; census: DashCensus } {
  const findings: CleanupFinding[] = [];
  const census: DashCensus = {};
  for (const d of list ?? dashList(s)) {
    if (d.style) census[d.style] = (census[d.style] ?? 0) + 1;
    // Only the sides that touch words are spaced to the style; beside a quotation mark or a line's end, the spacing
    // stays as the writer set it.
    const leftSpaces = s.slice(d.range[0], d.dash[0]);
    const rightSpaces = s.slice(d.dash[1], d.range[1]);
    const spaceChar = leftSpaces[0] ?? ' ';
    const left = d.wordBefore ? (isSpaced(style) ? spaceChar : '') : leftSpaces.join('');
    const right = d.wordAfter ? (isSpaced(style) ? ' ' : '') : rightSpaces.join('');
    const replacement = left + dashOf(style) + right;
    if (replacement === fromScalars(s as string[], d.range[0], d.range[1])) continue;
    const message = d.hyphens ? 'Hyphens for a dash.' : `A dash set differently from the manuscript's style (${dashStyleExample(style)}).`;
    findings.push({ check: 'dashes', range: d.range, replacement, message });
  }
  return { findings, census };
}

/** `DashStyle.census`: how many dashes between words each style has in these texts. */
export function dashCensus(texts: readonly string[]): DashCensus {
  const total: DashCensus = {};
  for (const text of texts) {
    for (const [style, n] of Object.entries(dashes(toScalars(text), 'closed').census)) total[style as DashStyle] = (total[style as DashStyle] ?? 0) + n;
  }
  return total;
}

/** `DashStyle.usual(in:)`: the style most of the dashes use (closed up when there are none). */
export const usualDashStyle = (texts: readonly string[]): DashStyle => usualFromCensus(dashCensus(texts));

// MARK: Lines

/** Blank as the dialect's parser sees it: nothing but `CharacterSet.whitespaces`. */
function isBlank(s: readonly string[], [lo, hi]: [number, number]): boolean {
  for (let k = lo; k < hi; k++) if (!isCFWhitespace(s[k].codePointAt(0)!)) return false;
  return true;
}

/** List items' markers ("•⇥", "3.⇥"), by where their line starts: how many scalars the marker and its tab take. A block's
 *  lines are items when its first is one, as the dialect's parser reads them. */
export function listMarkers(s: readonly string[]): Map<number, number> {
  const markers = new Map<number, number>();
  let inList = false, blockStart = true, afterBreak = false;
  for (const line of scalarLines(s)) {
    if (isBlank(s, line)) { blockStart = true; inList = false; continue; }
    const text = fromScalars(s as string[], line[0], line[1]);
    const marker = parseListMarker(text);
    if (marker && (blockStart || (inList && !afterBreak))) {
      inList = true;
      markers.set(line[0], toScalars(graphemes(text).slice(0, marker.length).join('')).length);
    } else if (blockStart) {
      inList = false;
    }
    blockStart = false;
    afterBreak = text.endsWith('\\') && !text.endsWith('\\\\');
  }
  return markers;
}

/** The `]{…}` closing a span at `i` (classes the dialect knows), as its length in scalars. */
export function spanClose(s: readonly string[], i: number, end: number): number | null {
  if (s[i] !== ']' || !(i + 1 < end) || s[i + 1] !== '{') return null;
  let brace = -1;
  for (let k = i + 2; k < end; k++) if (s[k] === '}') { brace = k; break; }
  if (brace < 0 || spanStyle(fromScalars(s as string[], i + 1, brace + 1)) === null) return null;
  return brace - i + 1;
}

// MARK: Spaces

const SPACE_LIKE: ReadonlySet<string> = new Set([' ', '\t', '\u00A0']);

function spaces(s: string[], o: Options): CleanupFinding[] {
  const lines = scalarLines(s);
  // Blank lines the empty-lines check removes anyway.
  const removed = o.checks.has('empty-lines') ? emptyLines(s).lines : new Set<number>();
  const markers = listMarkers(s);
  const verse = new Set(verseLines(s).map((l) => l[0]));
  const out: CleanupFinding[] = [];
  const add = (range: [number, number], replacement: string, message: string) => out.push({ check: 'spaces', range, replacement, message });
  lines.forEach((line, n) => {
    const [lo, hi] = line;
    if (hi === lo) return;
    if (isBlank(s, line)) {
      if (!removed.has(n)) add(line, '', 'A blank line with spaces in it.');
      return;
    }
    let start = lo;
    if (verse.has(start)) {
      // A line of verse: its leading tabs are its steps, and spaces there are `verseSpaces`' to turn into steps.
      while (start < hi && SPACE_LIKE.has(s[start])) start++;
    }
    if (s[start] === '>') { // a block quote's marker keeps its space
      start++;
      if (start < hi && s[start] === ' ') start++;
    }
    start += markers.get(lo) ?? 0; // a list item's marker keeps its tab
    let first = start;
    while (first < hi && SPACE_LIKE.has(s[first])) first++;
    if (first > start) add([start, first], '', 'Spaces at the start of a paragraph.');
    let end = hi;
    while (end > first && SPACE_LIKE.has(s[end - 1])) end--;
    if (end < hi) add([end, hi], '', 'Spaces at the end of a paragraph.');
    let i = first;
    while (i < end) {
      if (!SPACE_LIKE.has(s[i])) { i++; continue; }
      let j = i;
      while (j < end && SPACE_LIKE.has(s[j])) j++;
      if (j - i > 1 || s[i] === '\t') {
        add([i, j], s.slice(i, j).includes('\u00A0') ? '\u00A0' : ' ', j - i > 1 ? `${j - i} spaces in a row.` : 'A tab.');
      }
      i = j;
    }
  });
  return out;
}

// MARK: Empty lines

/** Runs of blank lines beyond the one that separates two blocks, and any before the first block or after the last;
 *  with the indices of the lines they remove. */
function emptyLines(s: string[]): { findings: CleanupFinding[]; lines: Set<number> } {
  const lines = scalarLines(s);
  const firstContent = lines.findIndex((l) => !isBlank(s, l));
  let lastContent = -1;
  for (let k = lines.length - 1; k >= 0; k--) if (!isBlank(s, lines[k])) { lastContent = k; break; }
  if (firstContent < 0 || lastContent < 0) return { findings: [], lines: new Set() };
  const findings: CleanupFinding[] = [];
  const removed = new Set<number>();
  if (firstContent > 0) {
    findings.push({ check: 'empty-lines', range: [0, lines[firstContent][0]], replacement: '', message: 'Empty lines before the first paragraph.' });
    for (let k = 0; k < firstContent; k++) removed.add(k);
  }
  if (lines.length - 1 - lastContent >= 2) {
    findings.push({ check: 'empty-lines', range: [lines[lastContent][1], s.length], replacement: '', message: 'Empty lines after the last paragraph.' });
    for (let k = lastContent + 1; k < lines.length; k++) removed.add(k);
  }
  let n = firstContent + 1;
  while (n < lastContent) {
    if (!isBlank(s, lines[n])) { n++; continue; }
    let end = n;
    while (isBlank(s, lines[end + 1])) end++;
    const extra = end - n;
    if (extra > 0) {
      findings.push({
        check: 'empty-lines', range: [lines[n][0], lines[end + 1][0]], replacement: '\n',
        message: extra === 1 ? 'An extra empty line between paragraphs.' : `${extra} extra empty lines between paragraphs.`,
      });
      for (let k = n; k <= end; k++) removed.add(k);
    }
    n = end + 1;
  }
  return { findings, lines: removed };
}

// MARK: Dialogue quotes

const SPEECH_PUNCTUATION: ReadonlySet<string> = new Set(['.', ',', '!', '?', ';', ':', '—', '–', '…']);
/** What may come before a speech: nothing, the end of a sentence, an introduction, another quote. */
const BEFORE_SPEECH: ReadonlySet<string> = new Set(['.', '!', '?', ':', ',', '—', '–', '…', '(', '“', '”', '‘', '’']);

const inLine = (q: Quotation, j: number) => j >= q.line[0] && j < q.line[1];

/** Whether a quotation reads as speech rather than a title or a word in quotes (`pressed ‘Play’`). Speech starts a
 *  paragraph or sentence, or follows an introduction, and ends with punctuation; or, mid-sentence, ends with ! or ?, or
 *  with punctuation after two words or more. */
export function isSpeech(q: Quotation, c: readonly string[]): boolean {
  const significant = (i: number, step: number) => {
    let j = i + step;
    while (inLine(q, j) && QUOTE_MARKERS.has(c[j])) j += step;
    return inLine(q, j) ? c[j] : undefined;
  };
  let before = q.open - 1;
  while (inLine(q, before) && (isWhitespace(c[before]) || QUOTE_MARKERS.has(c[before]))) before--;
  const startsSpeech = !inLine(q, before) || BEFORE_SPEECH.has(c[before]);
  if (q.close === null) return startsSpeech;
  const inside = significant(q.close, -1);
  const punctuatedInside = inside !== undefined && SPEECH_PUNCTUATION.has(inside);
  if (startsSpeech) {
    const after = significant(q.close, 1);
    return punctuatedInside || (after !== undefined && SPEECH_PUNCTUATION.has(after));
  }
  if (!punctuatedInside) return false;
  if (inside === '!' || inside === '?') return true;
  let words = 0;
  let inWord = false;
  for (let k = q.open + 1; k < q.close; k++) {
    const x = c[k];
    const letter = isAlphabetic(x);
    if (letter && !inWord) words++;
    inWord = letter || (inWord && (x === '’' || x === '-'));
  }
  return words >= 2;
}

function dialogueQuotes(s: string[], style: QuoteStyle): CleanupFinding[] {
  const c = curled(s);
  const quotes = quotations(c);
  const out: CleanupFinding[] = [];
  for (const q of quotes) {
    // A quote inside another follows the outermost one: switched only if that is speech.
    const outer = q.depth === 0 ? q : quotes.find((o) => o.depth === 0 && o.line[0] === q.line[0] && o.line[1] === q.line[1] && o.open < q.open && o.end > q.open);
    if (!outer || !isSpeech(outer, c)) continue;
    const wantDouble = (q.depth % 2 === 0) === (style === 'double');
    if (q.double === wantDouble) continue;
    const message = q.depth === 0
      ? (wantDouble ? 'Dialogue in single quotes.' : 'Dialogue in double quotes.')
      : (wantDouble ? 'A quote inside dialogue in single quotes.' : 'A quote inside dialogue in double quotes.');
    out.push({ check: 'dialogue-quotes', range: [q.open, q.open + 1], replacement: wantDouble ? '“' : '‘', message });
    if (q.close !== null) out.push({ check: 'dialogue-quotes', range: [q.close, q.close + 1], replacement: wantDouble ? '”' : '’', message });
  }
  return out;
}

// MARK: Capitals

/** Words a full stop follows without ending the sentence (lowercased). */
export const ABBREVIATIONS: ReadonlySet<string> = new Set([
  'mr', 'mrs', 'ms', 'mx', 'dr', 'st', 'jr', 'sr', 'prof', 'rev', 'hon', 'gen', 'col', 'capt', 'lt', 'sgt', 'cpl',
  'mt', 'ave', 'rd', 'co', 'inc', 'ltd', 'vs', 'etc', 'cf', 'approx', 'vol', 'ch', 'fig', 'pp', 'ft', 'oz', 'lb',
  'lbs', 'dept', 'est', 'al', 'viz', 'ibid', 'messrs', 'mme', 'mlle',
  'jan', 'feb', 'apr', 'jun', 'jul', 'aug', 'sep', 'sept', 'oct', 'nov', 'dec', 'mon', 'tue', 'tues', 'thu', 'thur', 'thurs', 'fri',
]);

type Start = 'paragraph' | 'sentence' | 'speech' | 'continuedParagraph' | 'afterQuestion' | 'speechAfterComma';

const isCertain = (s: Start) => s !== 'afterQuestion' && s !== 'speechAfterComma' && s !== 'continuedParagraph';

const START_MESSAGE: Record<Start, string> = {
  paragraph: 'A paragraph that starts with a small letter.',
  continuedParagraph: "A paragraph that starts with a small letter after one that doesn't end a sentence. Does it carry that one on?",
  sentence: 'A sentence that starts with a small letter.',
  speech: 'Speech that starts with a small letter.',
  afterQuestion: 'A small letter after a question or exclamation mark. Does a new sentence start here?',
  speechAfterComma: 'Quoted words after a comma that start with a small letter. Are they a sentence of their own?',
};

function capitals(original: string[]): CleanupFinding[] {
  const s = curled(original);
  const quotes = quotations(s);
  const opens = new Map<number, Quotation>();
  const closes = new Set<number>();
  for (const q of quotes) {
    opens.set(q.open, q);
    if (q.close !== null) closes.add(q.close);
  }
  const markers = listMarkers(s);
  const out: CleanupFinding[] = [];
  let expect: Start | null = null;
  let quoteClosedInSentence = false; // `“I think,” she said, “that…”` carries on
  let lastSignificant: string | undefined;
  let lastWord = '', lastWordEnd = -1;
  let blockStart = true, inEpigraph = false, previousWasQuote = false;
  /** Whether the last paragraph ended a sentence (null at the start, or after a scene break). */
  let endedSentence: boolean | null = null;

  const significantBefore = (i: number) => {
    let j = i - 1;
    while (j >= 0 && QUOTE_MARKERS.has(s[j])) j--;
    return j >= 0 ? s[j] : undefined;
  };
  /** Whether quoted words that begin here start a sentence. */
  const speechStart = (q: Quotation): Start | null => {
    switch (lastSignificant) {
      case ':': return 'speech';
      case ',': case '—': case '–': {
        if (quoteClosedInSentence) return null;
        if (q.close === null) return 'speechAfterComma';
        switch (significantBefore(q.close)) {
          case '.': case '!': case '?': return 'speech';
          case ',': case '—': case '–': case '…': return 'speechAfterComma';
          default: return null;
        }
      }
      default: return null;
    }
  };
  const matches = (i: number, pattern: string) => {
    const p = toScalars(pattern);
    if (i + p.length > s.length) return false;
    return p.every((x, k) => s[i + k] === x);
  };

  for (const line of scalarLines(s)) {
    const [lo, hi] = line;
    const trimmed = trimCFWhitespace(fromScalars(s, lo, hi));
    if (inEpigraph) { // quoted from elsewhere, often verse
      if (trimmed === ':::') { inEpigraph = false; blockStart = true; endedSentence = null; }
      continue;
    }
    if (trimmed === '::: epigraph' || paragraphFence(trimmed)?.kind.type === 'verse') { inEpigraph = true; continue; }
    if (trimmed.startsWith(':::')) { blockStart = true; continue; } // an aligned block's fences
    if (trimmed === '***' || trimmed === '* * *') endedSentence = null;
    if (trimmed === '' || trimmed === '***' || trimmed === '* * *') { blockStart = true; continue; }

    let i = lo;
    const isQuote = s[i] === '>';
    if (isQuote !== previousWasQuote) blockStart = true;
    previousWasQuote = isQuote;
    if (isQuote) {
      i++;
      if (i < hi && s[i] === ' ') i++;
    }
    if (blockStart) {
      blockStart = false;
      expect = endedSentence ?? true ? 'paragraph' : 'continuedParagraph';
      quoteClosedInSentence = false;
      lastSignificant = undefined;
      if (matches(i, '[^')) {
        for (let k = i; k < hi; k++) if (matches(k, ']:')) { i = k + 2; break; }
      }
    }
    const marker = markers.get(lo);
    if (marker !== undefined) { // a list's items may start in lower case
      i += marker;
      expect = null;
    }
    while (i < hi) {
      const ch = s[i];
      if (isWhitespace(ch) || ch === '*') { i++; continue; }
      if (ch === '\\') { // an escaped mark isn't a letter
        expect = null;
        lastSignificant = i + 1 < hi ? s[i + 1] : ch;
        i += 2;
        continue;
      }
      if (matches(i, '[^')) {
        let close = -1;
        for (let k = i; k < hi; k++) if (s[k] === ']') { close = k; break; }
        if (close >= 0) { i = close + 1; continue; }
      }
      if (ch === '[') { i++; continue; } // a span opens
      const span = spanClose(s, i, hi);
      if (span !== null) { i += span; continue; }
      const q = opens.get(i);
      if (q) {
        if (expect === null) expect = speechStart(q); else if (expect === 'paragraph' || expect === 'sentence') expect = 'speech';
        lastSignificant = ch;
        i++;
        continue;
      }
      if (closes.has(i)) {
        const before = significantBefore(i);
        if (before !== undefined && ([',', '—', '–', '…'].includes(before) || isAlphabetic(before))) quoteClosedInSentence = true;
        expect = null; // a tag may follow: `“Stop!” she said.`
        lastSignificant = ch;
        i++;
        continue;
      }
      if (isAlphabetic(ch) || isDigit(ch)) {
        let j = i;
        let dotted = false;
        while (j < hi) {
          const x = s[j];
          if (isAlphabetic(x) || isDigit(x) || (x === '’' && !closes.has(j)) || x === '-') { j++; continue; }
          // Inner full stops belong to the word: e.g, U.S, 3.5.
          if (x === '.' && j + 1 < hi && (isAlphabetic(s[j + 1]) || isDigit(s[j + 1]))) {
            dotted = true;
            j++;
            continue;
          }
          break;
        }
        if (expect !== null) {
          const why = expect;
          const rest = s.slice(i + 1, j);
          if (isLowercase(ch) && !dotted && !rest.some(isUppercase)) {
            const title = titlecase(ch);
            if (title !== ch) out.push({ check: 'capitals', range: [i, i + 1], replacement: isCertain(why) ? title : null, message: START_MESSAGE[why] });
          }
          expect = null;
        }
        lastWord = fromScalars(s, i, j) + (dotted ? '.' : '');
        lastWordEnd = j;
        lastSignificant = s[j - 1];
        i = j;
        continue;
      }
      if (ch === '.' || ch === '!' || ch === '?') {
        let j = i;
        while (j < hi && (s[j] === '.' || s[j] === '!' || s[j] === '?')) j++;
        const run = s.slice(i, j);
        let k = j;
        while (k < hi && (s[k] === '*' || s[k] === ')' || s[k] === ']' || closes.has(k))) k += spanClose(s, k, hi) ?? 1;
        const terminal = k === hi || isWhitespace(s[k]);
        if (!terminal || (run.length > 1 && !run.some((x) => x !== '.'))) {
          expect = null; // 3.5, or an ellipsis trailing off
        } else if (run.includes('!') || run.includes('?')) {
          expect = 'afterQuestion';
          quoteClosedInSentence = false;
        } else {
          const w = toScalars(lastWord);
          const abbreviated = lastWordEnd === i && (ABBREVIATIONS.has(lastWord.toLowerCase()) || lastWord.includes('.') || (w.length === 1 && isAlphabetic(w[0])));
          expect = abbreviated ? null : 'sentence';
          if (!abbreviated) quoteClosedInSentence = false;
        }
        lastSignificant = s[j - 1];
        i = j;
        continue;
      }
      // `(` passes a sentence start on; anything else (dashes, ellipses, elisions) means it carries on.
      if (ch !== '(') expect = null;
      lastSignificant = ch;
      i++;
    }

    // (The Swift's `defer`:) whether this paragraph ended a sentence.
    let j = hi - 1;
    while (j > lo) {
      if (isWhitespace(s[j]) || QUOTE_MARKERS.has(s[j]) || closes.has(j) || s[j] === ')') { j--; continue; }
      // A span's `]{.u}` closes around the sentence's end.
      if (s[j] === '}') {
        let open = -1;
        for (let k = j - 1; k >= lo; k--) if (s[k] === ']') { open = k; break; }
        if (open >= 0 && spanClose(s, open, hi) === j - open + 1) { j = open - 1; continue; }
      }
      break;
    }
    endedSentence = j >= lo && ['.', '!', '?'].includes(s[j]);
  }
  return out;
}

// MARK: Typography (TextCleanup+Typography.swift)

/** Straight quotes and apostrophes, and curly quotes facing the wrong way (`QuoteMarks`). */
function curlyQuotes(s: string[]): CleanupFinding[] {
  const out: CleanupFinding[] = [];
  for (const p of analyseQuotes(s)) {
    if (!((p.kind === 'straight' || p.kind === 'wrong-way') && p.replacement !== null)) continue;
    const message = p.kind === 'wrong-way' ? quoteProblemMessage(p) : (p.replacement === '’' ? 'A straight apostrophe.' : 'A straight quotation mark.');
    out.push({ check: 'curly-quotes', range: [p.offset, p.offset + 1], replacement: p.replacement, message });
  }
  return out;
}

/** Feet and inches after a number set with quotation marks (`QuoteMarks`' primes). */
function primes(s: string[]): CleanupFinding[] {
  return analyseQuotes(s).filter((p) => p.kind === 'prime' && p.replacement !== null)
    .map((p) => ({ check: 'primes' as const, range: [p.offset, p.offset + 1] as [number, number], replacement: p.replacement, message: quoteProblemMessage(p) }));
}

/** The digits running forward from `i`, or with `back`, ending at `i`. */
function digitsAt(s: readonly string[], i: number, back = false): [number, number] {
  let j = i;
  if (back) {
    while (j > 0 && isDigit(s[j - 1])) j--;
    return [j, i];
  }
  while (j < s.length && isDigit(s[j])) j++;
  return [i, j];
}

/** Whether a hyphen at `h` (with `spaced` spaces either side) stands between two numbers that make a range: up to four
 *  digits each side, nothing joined on before the first nor after the second but a plural `s`, and not `555-1234`. */
function rangeHyphen(s: readonly string[], h: number, spaced: number): { left: [number, number]; right: [number, number] } | null {
  if (!(s[h] === '-' && h - spaced > 0 && h + spaced + 1 < s.length)) return null;
  if (spaced === 1 && (s[h - 1] !== ' ' || s[h + 1] !== ' ')) return null;
  const left = digitsAt(s, h - spaced, true), right = digitsAt(s, h + spaced + 1);
  const ln = left[1] - left[0], rn = right[1] - right[0];
  if (ln === 0 || rn === 0 || ln > 4 || rn > 4 || (ln === 3 && rn === 4)) return null;
  if (left[0] > 0) {
    const before = s[left[0] - 1];
    if (isAlphabetic(before) || ['-', '–', '.', ',', '/', '+', ':', '$', '£', '€', '#', '\\', '′'].includes(before)) return null;
  }
  let end = right[1];
  if (end < s.length && s[end] === 's') end++;
  if (end < s.length) {
    const after = s[end];
    if (isAlphabetic(after) || isDigit(after) || ['-', '–', '/', '′', '″', '%', '°'].includes(after)) return null;
    if (['.', ',', ':'].includes(after) && end + 1 < s.length && isDigit(s[end + 1])) return null;
  }
  return { left, right };
}

/** A hyphen between numbers that make a range (`1990-95`, `1990 - 1995`) takes an en dash, closed up. */
function enDashRanges(s: string[]): CleanupFinding[] {
  const out: CleanupFinding[] = [];
  for (let h = 0; h < s.length; h++) {
    if (s[h] !== '-') continue;
    if (rangeHyphen(s, h, 0) !== null) {
      out.push({ check: 'en-dash-ranges', range: [h, h + 1], replacement: '–', message: 'A hyphen in a range of numbers; ranges take an en dash.' });
    } else if (rangeHyphen(s, h, 1) !== null) {
      out.push({ check: 'en-dash-ranges', range: [h - 1, h + 2], replacement: '–', message: 'A spaced hyphen in a range of numbers; ranges take an en dash, closed up.' });
    }
  }
  return out;
}

/** An `x` between numbers (`3 x 5`, `3x5`, `2 x 4s`) is a multiplication sign: spaced both sides or neither, not hex
 *  (`0x1F`), nor after letters (`A4x`). */
function multiplication(s: string[]): CleanupFinding[] {
  const out: CleanupFinding[] = [];
  for (let i = 0; i < s.length; i++) {
    if (s[i] !== 'x' && s[i] !== 'X') continue;
    const spaced = i > 0 && i + 1 < s.length && s[i - 1] === ' ' && s[i + 1] === ' ';
    const gap = spaced ? 1 : 0;
    if (!(i - gap > 0 && i + gap + 1 < s.length && isDigit(s[i - gap - 1]) && isDigit(s[i + gap + 1]))) continue;
    const left = digitsAt(s, i - gap, true);
    if (left[0] > 0 && (isAlphabetic(s[left[0] - 1]) || s[left[0] - 1] === '\\')) continue;
    if (!spaced && left[1] - left[0] === 1 && s[left[0]] === '0') continue;
    const right = digitsAt(s, i + gap + 1);
    if (right[1] < s.length) {
      const after = s[right[1]];
      if (isAlphabetic(after) && !(after === 's' && (right[1] + 1 === s.length || !isAlphabetic(s[right[1] + 1])))) continue;
    }
    out.push({ check: 'multiplication', range: [i, i + 1], replacement: '×', message: 'An x between numbers; times takes the multiplication sign (×).' });
  }
  return out;
}

/** `(tm)` → ™ and `(r)` → ® after a word (the space before `(tm)` goes); `(c)` → © before a year (`(c) 2026`). */
function symbols(s: string[]): CleanupFinding[] {
  const out: CleanupFinding[] = [];
  const at = (i: number, text: string) => {
    const p = toScalars(text);
    if (i + p.length > s.length) return false;
    return p.every((c, k) => s[i + k].toLowerCase() === c);
  };
  for (let i = 0; i < s.length; i++) {
    if (!(s[i] === '(' && (i === 0 || s[i - 1] !== '\\'))) continue;
    if (at(i, '(tm)')) {
      let start = i;
      if (start > 1 && s[start - 1] === ' ' && isAlphabetic(s[start - 2])) start--;
      if (!(start > 0 && isAlphabetic(s[start - 1]))) continue;
      out.push({ check: 'symbols', range: [start, i + 4], replacement: '™', message: '(tm) for the trademark sign ™.' });
    } else if (at(i, '(r)') && i > 0 && isAlphabetic(s[i - 1]) && (i + 3 === s.length || !isAlphabetic(s[i + 3]))) {
      out.push({ check: 'symbols', range: [i, i + 3], replacement: '®', message: '(r) for the registered sign ®.' });
    } else if (at(i, '(c)') && i + 5 < s.length && s[i + 3] === ' ' && isDigit(s[i + 4])) {
      const year = digitsAt(s, i + 4);
      if (year[1] - year[0] === 4) out.push({ check: 'symbols', range: [i, i + 3], replacement: '©', message: '(c) for the copyright sign ©.' });
    }
  }
  return out;
}

/** Words in an underline span alone (`[words]{.u}`): books set emphasis in italics. The fix is `*words*`, unless the words
 *  hold emphasis already or the span sits inside a word. */
function underlining(s: string[]): CleanupFinding[] {
  const out: CleanupFinding[] = [];
  for (const [from, to] of spanRanges(s)) {
    let close = -1;
    for (let k = to - 1; k >= from; k--) if (s[k] === ']') { close = k; break; }
    if (close < 0 || fromScalars(s, close + 1, to) !== '{.u}') continue;
    const words = s.slice(from + 1, close);
    const joined = (from > 0 && isAlphabetic(s[from - 1])) || (to < s.length && isAlphabetic(s[to]));
    const fixable = !joined && !words.includes('*') && !words.includes('_') && words.length > 0
      && !isWhitespace(words[0]) && !isWhitespace(words[words.length - 1]);
    out.push({
      check: 'underlining', range: [from, to], replacement: fixable ? `*${words.join('')}*` : null,
      message: "Underlined words. Books set emphasis in italics; underlining is a typewriter's way of showing them.",
    });
  }
  return out;
}

// MARK: Mistakes (TextCleanup+Mistakes.swift)

const isWordScalar = (c: string | undefined) => isAlphabetic(c) || isDigit(c);

/** The words of a line: letters and digits, with apostrophes and hyphens inside them (`don’t`, `well-known`). */
export function wordsIn(s: readonly string[], [lo, hi]: [number, number]): [number, number][] {
  const out: [number, number][] = [];
  let i = lo;
  while (i < hi) {
    if (!isWordScalar(s[i])) { i++; continue; }
    let j = i + 1;
    while (j < hi) {
      if (isWordScalar(s[j])) { j++; continue; }
      if (['’', "'", '-'].includes(s[j]) && j + 1 < hi && isWordScalar(s[j + 1])) { j += 2; continue; }
      break;
    }
    out.push([i, j]);
    i = j;
  }
  return out;
}

/** Words a writer doubles on purpose (`had had`, `that that`, `bye bye`, `there there`), lowercased. */
const DOUBLES_MEANT: ReadonlySet<string> = new Set([
  'had', 'that', 'is', 'do', 'no', 'very', 'bye', 'there', 'so', 'well', 'ha', 'hee', 'ho', 'tut', 'knock', 'yes', 'now', 'oh',
  'ah', 'hey', 'la', 'blah', 'boo', 'chop', 'tick', 'dum', 'tsk', 'uh', 'um', 'hmm', 'mm', 'far', 'many', 'really', 'never',
  'please', 'out', 'round', 'on', 'go', 'come', 'wait', 'more', 'again', 'quick', 'dear', 'poor', 'hush', 'run', 'pom', 'rah',
  'woof', 'yeah', 'okay', 'ok', 'help', 'boom', 'bang', 'night', 'heh', 'hah', 'ga', 'na', 'nah', 'ta', 'ooh', 'aah',
  'goody', 'pretty', 'fast', 'slowly', 'faster',
]);

/** The same word twice in a row, with only spaces (or emphasis markers) between them: `the the`. Not names, both
 *  capitalised (`Duran Duran`, `The The`), nor a word said three times or more, which is meant. The fix keeps the first;
 *  with markers between (`the *the*`) it's listed, as dropping a word would break the emphasis. */
function doubledWords(s: string[]): CleanupFinding[] {
  const out: CleanupFinding[] = [];
  for (const line of scalarLines(s)) {
    const found = wordsIn(s, line);
    const repeats = (k: number): boolean => {
      if (!(k > 0 && k < found.length)) return false;
      const gap = s.slice(found[k - 1][1], found[k][0]);
      return gap.some((c) => c === ' ' || c === '\u00A0')
        && gap.every((c) => c === ' ' || c === '\u00A0' || c === '*' || c === '_')
        && fromScalars(s, ...found[k - 1]).toLowerCase() === fromScalars(s, ...found[k]).toLowerCase();
    };
    for (let k = 1; k < found.length; k++) {
      if (!(repeats(k) && !repeats(k - 1) && !repeats(k + 1))) continue;
      const a = found[k - 1], b = found[k];
      const first = fromScalars(s, ...a), word = first.toLowerCase();
      if (!toScalars(word).some(isAlphabetic) || DOUBLES_MEANT.has(word) || (isUppercase(s[a[0]]) && isUppercase(s[b[0]]))) continue;
      const markers = s.slice(a[1], b[0]).some((c) => c === '*' || c === '_');
      out.push({ check: 'doubled-words', range: [a[0], b[1]], replacement: markers ? null : first, message: `“${word}” twice in a row.` });
    }
  }
  return out;
}

/** Verbs that make a dialogue tag (`she said`, `asked Gary`): speaking, not actions such as smiled or laughed. */
const SPEECH_VERBS: ReadonlySet<string> = new Set([
  'said', 'says', 'say', 'asked', 'asks', 'replied', 'replies', 'answered', 'answers', 'whispered', 'whispers', 'shouted',
  'shouts', 'cried', 'cries', 'called', 'calls', 'muttered', 'mutters', 'murmured', 'murmurs', 'added', 'adds', 'continued',
  'continues', 'began', 'begins', 'explained', 'explains', 'insisted', 'insists', 'admitted', 'admits', 'agreed', 'agrees',
  'demanded', 'demands', 'snapped', 'snaps', 'told', 'tells', 'repeated', 'repeats', 'yelled', 'yells', 'screamed',
  'screams', 'growled', 'growls', 'hissed', 'hisses', 'groaned', 'protested', 'suggested', 'suggests', 'offered',
  'interrupted', 'declared', 'announced', 'observed', 'remarked', 'returned', 'rejoined', 'retorted', 'pleaded', 'begged',
  'warned', 'warns', 'conceded', 'breathed', 'mumbled', 'stammered', 'stuttered', 'exclaimed', 'lied', 'sang', 'wrote',
  'whimpered', 'barked', 'bellowed', 'ventured', 'persisted', 'prompted', 'corrected', 'confessed', 'reminded',
]);
const TAG_PRONOUNS: ReadonlySet<string> = new Set(['he', 'she', 'they', 'we', 'you', 'it', 'i']);

/** How a dialogue tag starts after a closing quote: `lower` for `she said`, `said Gary`, `he quietly said` (and `I said`);
 *  `capital` for `She said`. */
function tagStart(s: readonly string[], words: [number, number][]): 'lower' | 'capital' | null {
  if (words.length === 0) return null;
  const w1 = fromScalars(s as string[], ...words[0]);
  const lower = w1.toLowerCase();
  const verb = (k: number) => k < words.length && SPEECH_VERBS.has(fromScalars(s as string[], ...words[k]));
  const pronounTag = TAG_PRONOUNS.has(lower) && (verb(1) || (words.length > 2 && fromScalars(s as string[], ...words[1]).endsWith('ly') && verb(2)));
  if (w1 === lower) return pronounTag || SPEECH_VERBS.has(w1) ? 'lower' : null;
  if (w1 === 'I') return pronounTag ? 'lower' : null;
  return pronounTag && toScalars(w1).slice(1).every(isLowercase) ? 'capital' : null;
}

/** Speech and its tag punctuated as one sentence: a full stop before a tag (`“Stop.” he said` → comma), no punctuation at
 *  all (`“Stop” he said`), and a tag that starts with a capital (`“Stop,” She said`). Only quotations that start a speech
 *  and tags made of a pronoun or speech verb, so action beats and names are left alone. */
function dialoguePunctuation(s: string[]): CleanupFinding[] {
  const c = curled(s);
  const out: CleanupFinding[] = [];
  for (const q of quotations(c)) {
    if (q.depth !== 0 || q.close === null) continue;
    const close = q.close;
    let before = q.open - 1;
    while (inLine(q, before) && (isWhitespace(c[before]) || QUOTE_MARKERS.has(c[before]))) before--;
    if (inLine(q, before) && !BEFORE_SPEECH.has(c[before])) continue;
    let k = close - 1;
    while (k > q.open && QUOTE_MARKERS.has(c[k])) k--;
    if (!(k > q.open)) continue;
    let m = close + 1;
    while (m < q.line[1] && (c[m] === '*' || c[m] === '_')) m++;
    const gap = m;
    while (m < q.line[1] && c[m] === ' ') m++;
    if (!(m > gap && m < q.line[1] && isAlphabetic(c[m]))) continue;
    const following = wordsIn(c, [m, q.line[1]]).slice(0, 3);
    if (following.length === 0 || following[0][0] !== m) continue;
    const tag = tagStart(c, following);
    if (tag === null) continue;
    const inside = c[k];
    if (inside === '.' && tag === 'lower') {
      if (c[k - 1] === '.') continue; // an ellipsis of full stops
      const last = wordsIn(c, [q.open, k]).at(-1);
      const word = last ? fromScalars(c, ...last) : '';
      if (ABBREVIATIONS.has(word.toLowerCase())) continue;
      out.push({ check: 'dialogue-punctuation', range: [k, k + 1], replacement: ',', message: 'A full stop ends the speech, but the tag carries the sentence on: it takes a comma.' });
    } else if (tag === 'lower' && isWordScalar(inside)) {
      out.push({ check: 'dialogue-punctuation', range: [k, close + 1], replacement: fromScalars(s, k, close) + ',' + s[close], message: 'Speech runs into its tag with no punctuation: it takes a comma.' });
    } else if ((inside === ',' || inside === '?' || inside === '!') && tag === 'capital') {
      out.push({ check: 'dialogue-punctuation', range: [m, m + 1], replacement: c[m].toLowerCase(), message: 'The tag carries the sentence on, so it starts with a small letter.' });
    }
  }
  return out;
}

const PUNCTUATION_NAMES: Readonly<Record<string, string>> = {
  ',': 'comma', '.': 'full stop', ';': 'semicolon', ':': 'colon', '!': 'exclamation mark', '?': 'question mark',
};

/** A space before a comma, full stop, semicolon, colon, `!` or `?` (in French only commas and full stops); no space after a
 *  full stop, `!` or `?` that ends a sentence (`it.Then`) or after a comma or semicolon between words (`this,that`); and
 *  doubled commas or semicolons (fixed) or a full stop beside a comma or semicolon (listed; not after an abbreviation). */
function punctuationSpacing(s: string[], language: string | null): CleanupFinding[] {
  const french = (language ?? '').toLowerCase().startsWith('fr');
  const out: CleanupFinding[] = [];
  const name = (c: string) => PUNCTUATION_NAMES[c] ?? c;
  for (const line of scalarLines(s)) {
    const [lo, hi] = line;
    if (trimCFWhitespace(fromScalars(s, lo, hi)).startsWith(':::')) continue;
    let i = lo;
    while (i < hi) {
      const p = s[i];
      if (PUNCTUATION_NAMES[p] === undefined || (i !== lo && s[i - 1] === '\\')) { i++; continue; }
      const next = i + 1 < hi ? s[i + 1] : undefined;
      // A space before.
      let g = i;
      while (g > lo && (s[g - 1] === ' ' || s[g - 1] === '\t')) g--;
      if (g < i && g > lo && !(french && [';', ':', '!', '?'].includes(p))) {
        const word = s[g - 1];
        const ends = next === undefined || isWhitespace(next) || ['”', '’', '"', "'", '*', '_'].includes(next);
        const ellipsis = p === '.' && (next === '.' || word === '.' || (i + 2 < hi && s[i + 1] === ' ' && s[i + 2] === '.'));
        if (ends && !ellipsis && (isWordScalar(word) || ['”', '’', ')', '*', '_'].includes(word))) {
          out.push({ check: 'punctuation-spacing', range: [g, i], replacement: '', message: `A space before a ${name(p)}.` });
        }
      }
      // Two marks together.
      if (next !== undefined && [',', ';', '.'].includes(next) && [',', ';', '.'].includes(p) && !(p === '.' && next === '.')
        && (p !== '.' || i === lo || s[i - 1] !== '.') && (next !== '.' || i + 2 >= hi || s[i + 2] !== '.')) {
        const last = wordsIn(s, [lo, i]).at(-1);
        const word = last && last[1] === i ? fromScalars(s, ...last) : '';
        const abbreviated = p === '.' && (ABBREVIATIONS.has(word.toLowerCase()) || toScalars(word).length === 1);
        if (!abbreviated) {
          const same = p === next && p !== '.';
          out.push({
            check: 'punctuation-spacing', range: [i, i + 2], replacement: same ? p : null,
            message: same ? `Two ${name(p)}s together.` : `A ${name(p)} and a ${name(next)} together.`,
          });
          i += 2;
          continue;
        }
      }
      // No space after.
      if (next !== undefined && (i + 2 < hi || [',', ';'].includes(p))) {
        const last = wordsIn(s, [lo, i]).at(-1);
        const word = last && last[1] === i ? s.slice(last[0], last[1]) : null;
        if (word && word.length >= 2 && word.every(isLowercase) && !ABBREVIATIONS.has(word.join(''))) {
          if (['.', '!', '?'].includes(p) && isUppercase(next) && isLowercase(s[i + 2])) {
            out.push({ check: 'punctuation-spacing', range: [i, i + 1], replacement: p + ' ', message: `No space after a ${name(p)}.` });
          } else if ([',', ';'].includes(p) && isAlphabetic(next)) {
            const after = wordsIn(s, [i + 1, hi])[0];
            if (after && after[0] === i + 1 && after[1] - after[0] >= 2) {
              out.push({ check: 'punctuation-spacing', range: [i, i + 1], replacement: p + ' ', message: `No space after a ${name(p)}.` });
            }
          }
        }
      }
      i++;
    }
  }
  return out;
}

/** A round bracket opened and not closed in its paragraph, or closed with none open. Not a link's address, an escaped
 *  bracket, a smiley (`:)`) or an enumeration (`a)`, `2)`, a list's `1)⇥`). */
function brackets(s: string[]): CleanupFinding[] {
  const syntax = new Set<number>();
  for (const link of linkScalarRanges(s)) for (const [from, to] of link.syntax) for (let k = from; k < to; k++) syntax.add(k);
  const markers = listMarkers(s);
  const out: CleanupFinding[] = [];
  for (const [lo, hi] of scalarLines(s)) {
    const open: number[] = [];
    for (let i = lo + (markers.get(lo) ?? 0); i < hi; i++) {
      if (syntax.has(i) || (i !== lo && s[i - 1] === '\\')) continue;
      if (s[i] === '(') {
        open.push(i);
      } else if (s[i] === ')') {
        if (open.pop() !== undefined) continue;
        if (i > lo && [':', ';', '-', '='].includes(s[i - 1])) continue;
        const word = wordsIn(s, [lo, i]).at(-1);
        if (word && word[1] === i && (word[0] === lo || isWhitespace(s[word[0] - 1]) || s[word[0] - 1] === '*')
          && (word[1] - word[0] <= 2 || s.slice(word[0], word[1]).every((x) => 'ivxlc'.includes(x)))) continue;
        out.push({ check: 'brackets', range: [i, i + 1], replacement: null, message: 'A closing bracket with no opening one.' });
      }
    }
    for (const o of open) {
      if (o + 1 < hi && [':', ';'].includes(s[o + 1])) continue;
      out.push({ check: 'brackets', range: [o, o + 1], replacement: null, message: 'A bracket opened and never closed in its paragraph.' });
    }
  }
  return out;
}

/** `TextCleanup`, named as the Swift names it. */
export const TextCleanup = {
  checks: CLEANUP_CHECKS,
  check: checkText,
  fixed: fixedText,
  survey: surveyTexts,
  quotations,
  curled,
};

/** `DashStyle` census and usual style (the style's names are in dashStyle.ts). */
export const DashStyleCensus = { census: dashCensus, usual: usualDashStyle, usualFromCensus };
